import { useState, useEffect } from 'react';
import { Save, Image, Type, Settings, FileText, Home, Wrench, Paintbrush, Calendar, Phone, Globe } from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCMSFull = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('general');

  const getAuthHeader = () => {
    const password = localStorage.getItem('idmat_admin');
    return {
      headers: {
        'Authorization': 'Basic ' + btoa('admin:' + password)
      }
    };
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/admin/settings`, getAuthHeader());
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast.error('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, getAuthHeader());
      toast.success('Modifications enregistrées !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { id: 'general', label: 'Général', icon: <Settings size={18} /> },
    { id: 'home', label: 'Accueil', icon: <Home size={18} /> },
    { id: 'repair', label: 'Réparation', icon: <Wrench size={18} /> },
    { id: 'paint', label: 'Peinture', icon: <Paintbrush size={18} /> },
    { id: 'maintenance', label: 'Entretien', icon: <Calendar size={18} /> },
    { id: 'contact', label: 'Contact', icon: <Phone size={18} /> },
    { id: 'seo', label: 'SEO & Tracking', icon: <Globe size={18} /> },
  ];

  const InputField = ({ label, field, type = 'text', placeholder = '', multiline = false, rows = 3 }) => (
    <div className="mb-4">
      <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2 font-medium">
        {label}
      </label>
      {multiline ? (
        <textarea
          value={settings[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          rows={rows}
          className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
          placeholder={placeholder}
        />
      ) : (
        <input
          type={type}
          value={settings[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
          placeholder={placeholder}
        />
      )}
    </div>
  );

  const ImageField = ({ label, field }) => (
    <div className="mb-4">
      <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2 font-medium">
        {label}
      </label>
      <div className="flex gap-4">
        <input
          type="text"
          value={settings[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
          placeholder="URL de l'image"
        />
        {settings[field] && (
          <img 
            src={settings[field]} 
            alt="Preview" 
            className="w-16 h-16 object-cover border border-gray-200"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
      </div>
    </div>
  );

  const SectionTitle = ({ icon, title }) => (
    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
      <div className="w-10 h-10 bg-[#F5C500] flex items-center justify-center">
        {icon}
      </div>
      <h2 className="text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        {title}
      </h2>
    </div>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-[#F5C500] border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div data-testid="admin-cms-full">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <h1 className="text-3xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Gestion du Contenu (CMS)
          </h1>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-[#F5C500] text-[#1a1a1a] px-6 py-3 font-bold uppercase tracking-wide hover:bg-[#ddb200] transition-colors disabled:opacity-50"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            <Save size={18} />
            {saving ? 'Enregistrement...' : 'Enregistrer tout'}
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white shadow-sm sticky top-24">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    activeSection === section.id 
                      ? 'bg-[#F5C500] text-[#1a1a1a]' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {section.icon}
                  <span className="font-medium">{section.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1">
            {/* GÉNÉRAL */}
            {activeSection === 'general' && (
              <div className="bg-white shadow-sm p-6">
                <SectionTitle icon={<Settings size={20} color="#1a1a1a" />} title="Informations Générales" />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField label="Nom de l'entreprise" field="company_name" />
                  <InputField label="Slogan" field="slogan" />
                  <InputField label="Téléphone" field="phone" />
                  <InputField label="Email" field="email" type="email" />
                  <InputField label="Adresse" field="address" />
                  <InputField label="Ville / Code postal" field="city" />
                  <InputField label="Région" field="region" />
                  <InputField label="Horaires" field="hours" />
                  <InputField label="Années d'expérience" field="years_experience" placeholder="15+" />
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h3 className="font-bold text-[#1a1a1a] mb-4">Footer</h3>
                  <InputField label="Description footer" field="footer_description" multiline rows={2} />
                  <InputField label="Lien idmatoi.com" field="footer_idmatoi_link" />
                  <InputField label="Marques affichées" field="footer_brands" placeholder="LiuGong • Komatsu • Caterpillar" />
                </div>
              </div>
            )}

            {/* PAGE ACCUEIL */}
            {activeSection === 'home' && (
              <div className="space-y-6">
                {/* Hero */}
                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Home size={20} color="#1a1a1a" />} title="Section Hero" />
                  <InputField label="Titre principal" field="home_hero_title" />
                  <InputField label="Sous-titre" field="home_hero_subtitle" multiline />
                  <ImageField label="Image de fond" field="home_hero_image" />
                  <InputField label="Texte du bouton CTA" field="home_hero_cta" />
                </div>

                {/* Services */}
                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Wrench size={20} color="#1a1a1a" />} title="Section Services" />
                  <InputField label="Titre section" field="home_services_title" />
                  <InputField label="Sous-titre section" field="home_services_subtitle" multiline />
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                    <div className="p-4 bg-gray-50 border-t-4 border-[#F5C500]">
                      <h4 className="font-bold mb-3">Service 1</h4>
                      <InputField label="Titre" field="home_service1_title" />
                      <InputField label="Description" field="home_service1_desc" multiline rows={2} />
                    </div>
                    <div className="p-4 bg-gray-50 border-t-4 border-[#F5C500]">
                      <h4 className="font-bold mb-3">Service 2</h4>
                      <InputField label="Titre" field="home_service2_title" />
                      <InputField label="Description" field="home_service2_desc" multiline rows={2} />
                    </div>
                    <div className="p-4 bg-gray-50 border-t-4 border-[#F5C500]">
                      <h4 className="font-bold mb-3">Service 3</h4>
                      <InputField label="Titre" field="home_service3_title" />
                      <InputField label="Description" field="home_service3_desc" multiline rows={2} />
                    </div>
                  </div>
                </div>

                {/* Pourquoi nous */}
                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<FileText size={20} color="#1a1a1a" />} title="Section Pourquoi Nous" />
                  <InputField label="Titre" field="home_why_title" />
                  <InputField label="Description" field="home_why_subtitle" multiline />
                  <ImageField label="Image" field="home_why_image" />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <div className="p-3 bg-gray-50">
                      <InputField label="Raison 1 - Titre" field="home_reason1_title" />
                      <InputField label="Raison 1 - Texte" field="home_reason1_text" />
                    </div>
                    <div className="p-3 bg-gray-50">
                      <InputField label="Raison 2 - Titre" field="home_reason2_title" />
                      <InputField label="Raison 2 - Texte" field="home_reason2_text" />
                    </div>
                    <div className="p-3 bg-gray-50">
                      <InputField label="Raison 3 - Titre" field="home_reason3_title" />
                      <InputField label="Raison 3 - Texte" field="home_reason3_text" />
                    </div>
                    <div className="p-3 bg-gray-50">
                      <InputField label="Raison 4 - Titre" field="home_reason4_title" />
                      <InputField label="Raison 4 - Texte" field="home_reason4_text" />
                    </div>
                  </div>
                </div>

                {/* Galerie */}
                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Image size={20} color="#1a1a1a" />} title="Galerie Photos" />
                  <InputField label="Titre galerie" field="home_gallery_title" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ImageField label="Image 1" field="home_gallery_image1" />
                    <ImageField label="Image 2" field="home_gallery_image2" />
                    <ImageField label="Image 3" field="home_gallery_image3" />
                    <ImageField label="Image 4" field="home_gallery_image4" />
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Phone size={20} color="#1a1a1a" />} title="Section CTA (Appel à l'action)" />
                  <InputField label="Titre" field="home_cta_title" />
                  <InputField label="Sous-titre" field="home_cta_subtitle" multiline />
                </div>
              </div>
            )}

            {/* PAGE RÉPARATION */}
            {activeSection === 'repair' && (
              <div className="space-y-6">
                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Wrench size={20} color="#1a1a1a" />} title="Page Réparation - Hero" />
                  <InputField label="Titre" field="repair_hero_title" />
                  <InputField label="Sous-titre" field="repair_hero_subtitle" multiline />
                  <ImageField label="Image de fond" field="repair_hero_image" />
                </div>

                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Settings size={20} color="#1a1a1a" />} title="Engins & Marques" />
                  <InputField label="Titre section engins" field="repair_equipment_title" />
                  <InputField label="Marques (séparées par des virgules)" field="repair_brands" placeholder="LiuGong,Komatsu,Caterpillar" />
                </div>

                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<FileText size={20} color="#1a1a1a" />} title="Processus d'intervention" />
                  <InputField label="Étape 1 - Diagnostic" field="repair_process_step1" multiline rows={2} />
                  <InputField label="Étape 2 - Devis" field="repair_process_step2" multiline rows={2} />
                  <InputField label="Étape 3 - Réparation" field="repair_process_step3" multiline rows={2} />
                  <InputField label="Étape 4 - Contrôle" field="repair_process_step4" multiline rows={2} />
                </div>
              </div>
            )}

            {/* PAGE PEINTURE */}
            {activeSection === 'paint' && (
              <div className="space-y-6">
                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Paintbrush size={20} color="#1a1a1a" />} title="Page Peinture - Hero" />
                  <InputField label="Titre" field="paint_hero_title" />
                  <InputField label="Sous-titre" field="paint_hero_subtitle" multiline />
                  <ImageField label="Image de fond" field="paint_hero_image" />
                </div>

                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<FileText size={20} color="#1a1a1a" />} title="Types de peinture" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-gray-50 border-t-4 border-[#F5C500]">
                      <InputField label="Type 1 - Titre" field="paint_type1_title" />
                      <InputField label="Type 1 - Description" field="paint_type1_desc" multiline rows={2} />
                    </div>
                    <div className="p-4 bg-gray-50 border-t-4 border-[#F5C500]">
                      <InputField label="Type 2 - Titre" field="paint_type2_title" />
                      <InputField label="Type 2 - Description" field="paint_type2_desc" multiline rows={2} />
                    </div>
                    <div className="p-4 bg-gray-50 border-t-4 border-[#F5C500]">
                      <InputField label="Type 3 - Titre" field="paint_type3_title" />
                      <InputField label="Type 3 - Description" field="paint_type3_desc" multiline rows={2} />
                    </div>
                  </div>
                </div>

                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Image size={20} color="#1a1a1a" />} title="Section Zones & Galerie" />
                  <InputField label="Titre section zones" field="paint_zones_title" />
                  <ImageField label="Image section zones" field="paint_zones_image" />
                  
                  <h3 className="font-bold mt-6 mb-4">Images galerie</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ImageField label="Image 1" field="paint_gallery_image1" />
                    <ImageField label="Image 2" field="paint_gallery_image2" />
                    <ImageField label="Image 3" field="paint_gallery_image3" />
                    <ImageField label="Image 4" field="paint_gallery_image4" />
                    <ImageField label="Image 5" field="paint_gallery_image5" />
                    <ImageField label="Image 6" field="paint_gallery_image6" />
                  </div>
                </div>
              </div>
            )}

            {/* PAGE ENTRETIEN */}
            {activeSection === 'maintenance' && (
              <div className="space-y-6">
                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Calendar size={20} color="#1a1a1a" />} title="Page Entretien - Hero" />
                  <InputField label="Titre" field="maintenance_hero_title" />
                  <InputField label="Sous-titre" field="maintenance_hero_subtitle" multiline />
                  <ImageField label="Image de fond" field="maintenance_hero_image" />
                </div>

                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<FileText size={20} color="#1a1a1a" />} title="Types de contrats" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-gray-50 border-t-4 border-[#F5C500]">
                      <InputField label="Contrat 1 - Titre" field="maintenance_contract1_title" />
                      <InputField label="Contrat 1 - Description" field="maintenance_contract1_desc" multiline rows={2} />
                    </div>
                    <div className="p-4 bg-gray-50 border-t-4 border-[#F5C500]">
                      <InputField label="Contrat 2 - Titre" field="maintenance_contract2_title" />
                      <InputField label="Contrat 2 - Description" field="maintenance_contract2_desc" multiline rows={2} />
                    </div>
                    <div className="p-4 bg-gray-50 border-t-4 border-[#F5C500]">
                      <InputField label="Contrat 3 - Titre" field="maintenance_contract3_title" />
                      <InputField label="Contrat 3 - Description" field="maintenance_contract3_desc" multiline rows={2} />
                    </div>
                  </div>
                </div>

                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Image size={20} color="#1a1a1a" />} title="Section Avantages" />
                  <ImageField label="Image section avantages" field="maintenance_benefits_image" />
                </div>
              </div>
            )}

            {/* PAGE CONTACT */}
            {activeSection === 'contact' && (
              <div className="space-y-6">
                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Phone size={20} color="#1a1a1a" />} title="Page Contact - Hero" />
                  <InputField label="Titre" field="contact_hero_title" />
                  <InputField label="Sous-titre" field="contact_hero_subtitle" multiline />
                  <ImageField label="Image de fond" field="contact_hero_image" />
                </div>

                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<FileText size={20} color="#1a1a1a" />} title="Formulaire & Zone" />
                  <InputField label="Titre formulaire" field="contact_form_title" />
                  <InputField label="Texte zone d'intervention" field="contact_zone_text" multiline />
                </div>

                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Calendar size={20} color="#1a1a1a" />} title="Page Prestations" />
                  <InputField label="Titre" field="prestations_hero_title" />
                  <InputField label="Sous-titre" field="prestations_hero_subtitle" multiline />
                </div>
              </div>
            )}

            {/* SEO & TRACKING */}
            {activeSection === 'seo' && (
              <div className="space-y-6">
                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Globe size={20} color="#1a1a1a" />} title="SEO (Référencement)" />
                  <InputField label="Titre Meta (balise title)" field="meta_title" />
                  <p className="text-xs text-gray-400 mb-4">{(settings.meta_title || '').length}/60 caractères recommandés</p>
                  <InputField label="Description Meta" field="meta_description" multiline rows={3} />
                  <p className="text-xs text-gray-400">{(settings.meta_description || '').length}/160 caractères recommandés</p>
                </div>

                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Settings size={20} color="#1a1a1a" />} title="Pixels de Tracking" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField label="Meta Pixel (Facebook)" field="meta_pixel_id" placeholder="Ex: 1234567890123456" />
                    <InputField label="Google Analytics (GA4)" field="google_analytics_id" placeholder="Ex: G-XXXXXXXXXX" />
                    <InputField label="Google Tag Manager" field="google_tag_manager_id" placeholder="Ex: GTM-XXXXXXX" />
                    <InputField label="TikTok Pixel" field="tiktok_pixel_id" placeholder="Ex: CXXXXXXXXXXXXXXXXX" />
                    <InputField label="LinkedIn Insight Tag" field="linkedin_pixel_id" placeholder="Ex: 1234567" />
                  </div>
                </div>

                <div className="bg-white shadow-sm p-6">
                  <SectionTitle icon={<Settings size={20} color="#1a1a1a" />} title="Email (SendGrid)" />
                  <InputField label="Clé API SendGrid" field="sendgrid_api_key" type="password" placeholder="SG.xxxxx..." />
                  <InputField label="Email de notification" field="notification_email" type="email" placeholder="admin@votresite.com" />
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200">
                    <p className="text-sm text-yellow-800">
                      <strong>Note :</strong> Configurez SendGrid pour recevoir automatiquement un email à chaque nouvelle demande de devis.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCMSFull;
